import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  POWER_BI_GEO360_URLS,
  type PowerBiGeo360ReportKey,
} from './powerBiGeo360PermissionUrls';
import './style.css';

export function isReferrerFromGeo360(referrer: string): boolean {
  return (
    referrer.includes('app.geo360.com.br') ||
    referrer.includes('geo360.topocart.dev.br') ||
    referrer.includes('apps.geo360.topocart.dev.br') ||
    referrer.includes('sig-goiania.geo360.com.br') ||
    referrer.includes('sig.geo360.com.br') ||
    referrer.includes('web.geo360.com.br')
  );
}

export function geo360HomeUrlFromReferrer(referrer: string): string {
  const trimmed = referrer.trim();
  if (trimmed) {
    try {
      return `${new URL(trimmed).origin}/`;
    } catch {
      const originMatch = trimmed.match(/^(https?:\/\/[^/]+)/i);
      if (originMatch) return `${originMatch[1]}/`;
    }
    if (
      trimmed.includes('geo360.topocart.dev.br') ||
      trimmed.includes('apps.geo360.topocart.dev.br')
    ) {
      return 'https://geo360.topocart.dev.br/';
    }
    if (trimmed.includes('app.geo360.com.br')) {
      return 'https://app.geo360.com.br/';
    }
    if (trimmed.includes('.geo360.com.br')) {
      const hostMatch = trimmed.match(/(?:https?:\/\/)?([a-z0-9.-]+\.geo360\.com\.br)/i);
      if (hostMatch) return `https://${hostMatch[1]}/`;
    }
  }
  if (isLocalDevHost() || process.env.NODE_ENV === 'development') {
    return 'https://geo360.topocart.dev.br/';
  }
  return 'https://app.geo360.com.br/';
}

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

function isEntryReferrerOkForGeo360(referrer: string): boolean {
  return !referrer.trim() || isReferrerFromGeo360(referrer);
}

export function geo360BiSessionKey(pathname: string): string {
  return `geo360-pbi-admtenant:${pathname}`;
}

export function clearGeo360BiSessionForPath(pathname: string): void {
  sessionStorage.removeItem(geo360BiSessionKey(pathname));
}

const GEO360_PENDING_ONCE_KEY = 'geo360-pbi-pending-once';

type Geo360PendingPayload = {
  path: string;
  authToken: string;
  tnToken: string;
  referrer: string;
};

export function hasIntranetLdapSession(): boolean {
  if (!localStorage.getItem('accessToken')) return false;
  return (
    localStorage.getItem('isLoggedIn') === 'true' ||
    sessionStorage.getItem('isLoggedIn') === 'true'
  );
}

export function clearIntranetLdapAndSharedGeo360State(): void {
  const localKeys = ['accessToken', 'isLoggedIn', 'username', 'lastRoute'];
  localKeys.forEach((k) => localStorage.removeItem(k));
  const sessionKeys = ['refreshToken', 'isLoggedIn', 'username', 'lastRoute'];
  sessionKeys.forEach((k) => sessionStorage.removeItem(k));

  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (!key) continue;
    if (key.startsWith('geo360-pbi-admtenant:') || key === GEO360_PENDING_ONCE_KEY) {
      sessionStorage.removeItem(key);
    }
  }
}

const GEO360_READER_EDITOR_PERMS = new Set(['LEITOR', 'EDITOR', 'ADM', 'ADMTENANT']);

function isGeo360PermissionGranted(
  permissao: unknown,
  allowReaderEditor: boolean
): boolean {
  const role = String(permissao ?? '').trim().toUpperCase();  
  if (!role) return false;  
  if (allowReaderEditor) {
    return GEO360_READER_EDITOR_PERMS.has(role);
  }  
  return role === 'ADMTENANT' || role === 'ADM';
}

async function verifyTenantPermissionAny(
  urls: readonly string[],
  allowReaderEditor: boolean,
  authToken: string,
  tnToken: string
): Promise<boolean> {
  const checks = await Promise.all(
    urls.map(async (url) => {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-AUTH-TOKEN': authToken,
            'X-TN-TOKEN': tnToken,
          },
        });
        const data = await response.json();
        return isGeo360PermissionGranted(data?.permissao, allowReaderEditor);
      } catch {
        return false;
      }
    })
  );
  return checks.some(Boolean);
}

function AccessDeniedView({ referrer }: { referrer: string }) {
  const geo360Url = geo360HomeUrlFromReferrer(referrer);
  return (
    <div style={{ textAlign: 'center', marginTop: '50px' }}>
      <h1>Acesso negado</h1>
      <p>Você não possui permissão para acessar este relatório.</p>
      <a href={geo360Url}>
        <button type="button" className="return-geo360">
          Retornar ao Geo360
        </button>
      </a>
    </div>
  );
}

const REPORTS_ALLOWED_FOR_ALL: PowerBiGeo360ReportKey[] = [
  'Mariana2025',
  "Horizonte",
  "SantanaDoParaiso",
  "Vicosa",
  "SaoBorja",
  "Araraquara",
  "CaxiasDoSul",
  "Abaetetuba",
  "Picos",
  "PortopecemNelog",
  "Tucuma"
];

export function Geo360AdmTenantGate({
  reportKey,
  children,
}: {
  reportKey: PowerBiGeo360ReportKey;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [granted, setGranted] = useState(false);
  const [deniedReferrer, setDeniedReferrer] = useState('');

  const sessionKey = geo360BiSessionKey(location.pathname);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setGranted(false);

    const finishDenied = (referrerForReturn?: string) => {
      if (!cancelled) {
        setDeniedReferrer(
          referrerForReturn?.trim() ||
            (typeof document !== 'undefined' ? document.referrer : '')
        );
        setLoading(false);
      }
    };

    const finishGranted = () => {
      if (!cancelled) {
        setGranted(true);
        setLoading(false);
      }
    };

    const runGeo360Verify = async (
      authToken: string,
      tnToken: string,
      referrerForGeo: string,
      pathname: string
    ) => {
      if (!isEntryReferrerOkForGeo360(referrerForGeo)) {
        finishDenied(referrerForGeo);
        return;
      }

      const urls = POWER_BI_GEO360_URLS[reportKey];
      const allowReaderEditor = REPORTS_ALLOWED_FOR_ALL.includes(reportKey);

      const ok = await verifyTenantPermissionAny(
        urls,
        allowReaderEditor,
        authToken,
        tnToken
      );
      if (cancelled) return;

      if (ok) {
        sessionStorage.setItem(geo360BiSessionKey(pathname), '1');
        finishGranted();
      } else {
        finishDenied(referrerForGeo);
      }
    };

    const run = async () => {
      const params = new URLSearchParams(location.search);
      const urlAuth = params.get('authToken');
      const urlTn = params.get('tnToken');

      if (urlAuth && urlTn) {
        clearIntranetLdapAndSharedGeo360State();
        const payload: Geo360PendingPayload = {
          path: location.pathname,
          authToken: urlAuth,
          tnToken: urlTn,
          referrer: typeof document !== 'undefined' ? document.referrer : '',
        };
        sessionStorage.setItem(GEO360_PENDING_ONCE_KEY, JSON.stringify(payload));
        navigate(`${location.pathname}${location.hash || ''}`, { replace: true });
        return;
      }

      const rawPending = sessionStorage.getItem(GEO360_PENDING_ONCE_KEY);
      if (rawPending) {
        let pending: Geo360PendingPayload;
        try {
          pending = JSON.parse(rawPending) as Geo360PendingPayload;
        } catch {
          sessionStorage.removeItem(GEO360_PENDING_ONCE_KEY);
          finishDenied();
          return;
        }

        if (
          pending.path !== location.pathname ||
          !pending.authToken ||
          !pending.tnToken
        ) {
          sessionStorage.removeItem(GEO360_PENDING_ONCE_KEY);
          finishDenied(pending.referrer);
          return;
        }

        await runGeo360Verify(
          pending.authToken,
          pending.tnToken,
          pending.referrer,
          location.pathname
        );
        if (!cancelled && sessionStorage.getItem(GEO360_PENDING_ONCE_KEY) === rawPending) {
          sessionStorage.removeItem(GEO360_PENDING_ONCE_KEY);
        }
        return;
      }

      if (hasIntranetLdapSession()) {
        finishGranted();
        return;
      }

      if (sessionStorage.getItem(sessionKey) === '1') {
        finishGranted();
        return;
      }

      finishDenied();
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash, navigate, reportKey, sessionKey]);

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!granted) {
    return <AccessDeniedView referrer={deniedReferrer} />;
  }

  return <>{children}</>;
}
